import React, {
 useState, useMemo
} from 'react';
import {
    Collapsible, CollapsibleTrigger, CollapsibleContent,
} from 'shared/components/ui/collapsible';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from 'shared/components/ui/tooltip';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    ChevronRight, Cable, Database, Plus, Search, MoreHorizontal, Pencil, Trash2, Zap, Loader2, Unlink,
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu';
import {
 Connection
} from 'builders/builderApp/Connection/Components/Types/ConnectionTypes';
import {
 SchemaListItem
} from '../types/schema.types';
import {
 ConnectionTestState
} from './ConnectionsTable';

interface ConnectionTreeProps {
    connections: Connection[];
    schemas: SchemaListItem[];
    testStatuses: Record<string, ConnectionTestState>;
    selectedConnectionId: string | null;
    selectedSchemaName: string | null;
    onSelectConnection: (connectionId: string) => void;
    onSelectSchema: (schemaName: string, connectionId: string | null) => void;
    onNewConnection: () => void;
    onNewSchema: (connectionId: string | null) => void;
    onEditConnection: (connection: Connection) => void;
    onDeleteConnection: (connection: Connection) => void;
    onTestConnection: (connection: Connection) => void;
    onEditSchema: (schema: SchemaListItem) => void;
    onDeleteSchema: (schema: SchemaListItem) => void;
    onExpandConnection?: (connectionId: string) => void | Promise<void>;
}

const ConnectionTree: React.FC<ConnectionTreeProps> = ({
    connections, schemas, testStatuses,
    selectedConnectionId, selectedSchemaName,
    onSelectConnection, onSelectSchema,
    onNewConnection, onNewSchema,
    onEditConnection, onDeleteConnection, onTestConnection,
    onEditSchema, onDeleteSchema, onExpandConnection,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedConnections, setExpandedConnections] = useState<Set<string>>(new Set());
    const [loadingConnections, setLoadingConnections] = useState<Set<string>>(new Set());

    const schemasByConnection = useMemo(() => {
        const map = new Map<string, SchemaListItem[]>();
        const unlinked: SchemaListItem[] = [];

        schemas.forEach((schema) => {
            if (schema.connectionId) {
                const existing = map.get(schema.connectionId) || [];
                existing.push(schema);
                map.set(schema.connectionId, existing);
            } else {
                unlinked.push(schema);
            }
        });

        return {
 grouped: map,
unlinked
};
    }, [schemas]);

    const filteredConnections = useMemo(() => {
        if (!searchTerm) return connections;
        const term = searchTerm.toLowerCase();
        return connections.filter((c) =>
            c.connection_name.toLowerCase().includes(term) ||
            schemasByConnection.grouped.get(c.connection_id)?.some(
                (s) => s.tableName.toLowerCase().includes(term)
            )
        );
    }, [connections, searchTerm, schemasByConnection]);

    const filteredUnlinked = useMemo(() => {
        if (!searchTerm) return schemasByConnection.unlinked;
        const term = searchTerm.toLowerCase();
        return schemasByConnection.unlinked.filter((s) =>
            s.tableName.toLowerCase().includes(term)
        );
    }, [schemasByConnection.unlinked, searchTerm]);

    const toggleExpanded = async (connectionId: string) => {
        const isCurrentlyExpanded = expandedConnections.has(connectionId);
        setExpandedConnections((prev) => {
            const next = new Set(prev);
            if (isCurrentlyExpanded) {
                next.delete(connectionId);
            } else {
                next.add(connectionId);
            }
            return next;
        });

        if (!isCurrentlyExpanded && onExpandConnection) {
            setLoadingConnections((prev) => new Set(prev).add(connectionId));
            try {
                await onExpandConnection(connectionId);
            } finally {
                setLoadingConnections((prev) => {
                    const next = new Set(prev);
                    next.delete(connectionId);
                    return next;
                });
            }
        }
    };

    const renderTestDot = (connection: Connection) => {
        const testState = testStatuses[connection.connection_id];
        if (!testState) return <div className="h-2 w-2 rounded-full bg-gray-300 shrink-0" />;
        if (testState.status === 'testing') return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />;
        if (testState.status === 'success') return <div className="h-2 w-2 rounded-full bg-green-500 shrink-0" />;
        if (testState.status === 'error') return <div className="h-2 w-2 rounded-full bg-red-500 shrink-0" />;
        return <div className="h-2 w-2 rounded-full bg-gray-300 shrink-0" />;
    };

    const renderConnectionSchemas = (connectionId: string | null) => {
        const connectionSchemas = connectionId
            ? schemasByConnection.grouped.get(connectionId) || []
            : filteredUnlinked;

        return connectionSchemas.map((schema) => (
            <div
                key={schema.tableName}
                className={`group flex items-center gap-2 px-3 py-1.5 ml-4 text-sm cursor-pointer rounded-md hover:bg-accent ${
                    selectedSchemaName === schema.tableName ? 'bg-accent font-medium' : ''
                }`}
                onClick={() => onSelectSchema(schema.tableName, connectionId)}
            >
                <Database className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1">{schema.tableName}</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                    {schema.fieldCount}
                </Badge>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreHorizontal className="h-3 w-3" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-36">
                        <DropdownMenuItem onClick={() => onEditSchema(schema)}>
                            <Pencil className="mr-2 h-3.5 w-3.5" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => onDeleteSchema(schema)}
                        >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        ));
    };

    return (
        <TooltipProvider>
            <div className="flex flex-col h-full border-r">
                {/* Header */}
                <div className="p-3 border-b space-y-2">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Explorer</h3>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onNewConnection}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>New Connection</p></TooltipContent>
                        </Tooltip>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 h-8 text-sm"
                        />
                    </div>
                </div>

                {/* Tree */}
                <div className="flex-1 overflow-y-auto py-1">
                    {filteredConnections.length === 0 && filteredUnlinked.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-2 text-center px-4">
                            <Cable className="h-8 w-8 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground">
                                No connections yet
                            </p>
                            <Button size="sm" variant="outline" onClick={onNewConnection}>
                                <Plus className="mr-1 h-3.5 w-3.5" />
                                Add Connection
                            </Button>
                        </div>
                    ) : (
                        <>
                            {filteredConnections.map((conn) => {
                                const isExpanded = expandedConnections.has(conn.connection_id);
                                const connSchemas = schemasByConnection.grouped.get(conn.connection_id) || [];

                                return (
                                    <Collapsible
                                        key={conn.connection_id}
                                        open={isExpanded}
                                        onOpenChange={() => toggleExpanded(conn.connection_id)}
                                    >
                                        <div
                                            className={`group flex items-center gap-1.5 px-2 py-1.5 cursor-pointer hover:bg-accent ${
                                                selectedConnectionId === conn.connection_id && !selectedSchemaName
                                                    ? 'bg-accent'
                                                    : ''
                                            }`}
                                        >
                                            <CollapsibleTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 p-0">
                                                    <ChevronRight
                                                        className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                                    />
                                                </Button>
                                            </CollapsibleTrigger>
                                            {renderTestDot(conn)}
                                            <div
                                                className="flex-1 flex items-center gap-1.5 min-w-0"
                                                onClick={() => onSelectConnection(conn.connection_id)}
                                            >
                                                <Cable className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                                <span className="text-sm truncate">{conn.connection_name}</span>
                                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">
                                                    {conn.source_type}
                                                </Badge>
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MoreHorizontal className="h-3 w-3" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-44">
                                                    <DropdownMenuItem onClick={() => onNewSchema(conn.connection_id)}>
                                                        <Plus className="mr-2 h-3.5 w-3.5" />
                                                        New Schema
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => onTestConnection(conn)}>
                                                        <Zap className="mr-2 h-3.5 w-3.5" />
                                                        Test Connection
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => onEditConnection(conn)}>
                                                        <Pencil className="mr-2 h-3.5 w-3.5" />
                                                        Edit Connection
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-red-600"
                                                        onClick={() => onDeleteConnection(conn)}
                                                    >
                                                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        <CollapsibleContent>
                                            {loadingConnections.has(conn.connection_id) ? (
                                                <div className="ml-4 px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    Loading schemas...
                                                </div>
                                            ) : connSchemas.length > 0 ? (
                                                renderConnectionSchemas(conn.connection_id)
                                            ) : (
                                                <div className="ml-4 px-3 py-2 text-xs text-muted-foreground italic">
                                                    No schemas
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-xs h-auto p-0 ml-1"
                                                        onClick={() => onNewSchema(conn.connection_id)}
                                                    >
                                                        Add one
                                                    </Button>
                                                </div>
                                            )}
                                        </CollapsibleContent>
                                    </Collapsible>
                                );
                            })}

                            {/* Unlinked schemas */}
                            {filteredUnlinked.length > 0 && (
                                <Collapsible defaultOpen>
                                    <div className="group flex items-center gap-1.5 px-2 py-1.5 cursor-pointer hover:bg-accent mt-1 border-t">
                                        <CollapsibleTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 p-0">
                                                <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
                                            </Button>
                                        </CollapsibleTrigger>
                                        <Unlink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                        <span className="text-sm text-muted-foreground">Unlinked Schemas</span>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-auto">
                                            {filteredUnlinked.length}
                                        </Badge>
                                    </div>
                                    <CollapsibleContent>
                                        {renderConnectionSchemas(null)}
                                    </CollapsibleContent>
                                </Collapsible>
                            )}
                        </>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
};

export default ConnectionTree;
